import type { CourseProgress } from "@nyntax/contracts/schemas/course";

/**
 * Funnel events for the Academy (ticket 20). They follow the marketing site's
 * conventions (`apps/web/src/lib/analytics/events.ts`): event names are stable
 * snake_case and PostHog funnels key on them, so never rename one; add a new
 * name instead. The funnel is
 *
 * ```text
 * catalog viewed -> course viewed -> checkout started -> purchase paid
 * -> first lesson started
 * ```
 *
 * Properties carry only the public course slug. No name, email, phone or
 * Learner id is ever put in a property: who the Learner is comes from the
 * existing identity mechanism alone (`AnalyticsIdentity` calls PostHog's
 * `identify` with the account id and no properties, and PostHog's own
 * anonymous id covers visitors until they sign in).
 */
interface AcademyEvents {
  academy_catalog_viewed: Record<string, never>;
  academy_checkout_closed: { courseSlug: string };
  academy_checkout_started: { courseSlug: string };
  academy_course_viewed: { courseSlug: string };
  academy_first_lesson_started: { courseSlug: string };
  /** A Lesson video could not play (launch alert: client playback error rate). */
  academy_playback_error: { courseSlug: string; reason: PlaybackErrorReason };
  /** A Lesson video began playing; the denominator of the error rate. */
  academy_playback_started: { courseSlug: string };
  academy_purchase_paid: { courseSlug: string };
}

/** `media`: the element or link failed. `stall`: no data for over 8 s. */
export type PlaybackErrorReason = "media" | "stall";

/** Every Academy event name the tracker accepts. */
export type AcademyEventName = keyof AcademyEvents;

/** The properties that go with event `E`. */
export type AcademyEventProperties<E extends AcademyEventName> =
  AcademyEvents[E];

/** The slice of the PostHog client the tracker needs. */
export interface Capture {
  __loaded: boolean;
  capture: (event: string, properties: Record<string, unknown>) => void;
}

/**
 * Sends one event if PostHog has been initialised. Failures are swallowed
 * once the load has started; a synchronous throw from `load` still propagates
 * to the caller, as it did before.
 *
 * @param load - Resolves the PostHog client when the event is sent.
 * @param event - The event name.
 * @param properties - The event properties.
 */
const sendWhenLoaded = <E extends AcademyEventName>(
  load: () => Promise<Capture>,
  event: E,
  properties: AcademyEventProperties<E>
): void => {
  void load()
    .then((client) => {
      if (client.__loaded) {
        client.capture(event, properties);
      }
    })
    .catch(() => {
      // Best-effort: ignore a blocked or failed analytics chunk.
    });
};

/**
 * Builds `track` over a loader for the PostHog client. PostHog is imported on
 * demand and only captures once it has been initialised, which happens in
 * production only (`instrumentation-client.ts`), so dev and tests stay quiet.
 * Analytics is best-effort: a failed load never breaks a click.
 *
 * @param load - Resolves the PostHog client when the first event is sent.
 * @returns The `track` function, which sends one event once the client has loaded.
 */
export const createTrack =
  (load: () => Promise<Capture>) =>
  <E extends AcademyEventName>(
    event: E,
    properties: AcademyEventProperties<E>
  ): void => {
    sendWhenLoaded(load, event, properties);
  };

/**
 * The tracker the Academy uses. It loads PostHog on first use and captures
 * events only where PostHog has been initialised.
 */
export const track = createTrack(async () => {
  const { analytics } = await import("@nyntax/analytics/index");

  return analytics;
});

/**
 * True once the Learner has Started or Completed any of these Lessons. The
 * feed fires `academy_first_lesson_started` the first time this flips from
 * false (at load) to true, so a returning Learner never fires it again.
 *
 * @param progress - The Learner's progress for the Course.
 * @param lessonIds - The ids of the Course's Lessons.
 * @returns True when any of those Lessons is started or completed.
 */
export const hasStartedLesson = (
  progress: Pick<CourseProgress, "items">,
  lessonIds: ReadonlySet<string>
): boolean =>
  progress.items.some(
    (entry) => lessonIds.has(entry.itemId) && entry.state !== "not_started"
  );

/**
 * Playback health for the launch alert (ticket 21). One `started` per Lesson
 * per page view and one `error` per Lesson, reason and page view, so a Lesson
 * that keeps retrying counts once and the ratio errors / (started + errors)
 * reads as "share of Lesson plays that failed". The PostHog alert is created
 * by hand (see docs/runbooks/academy-launch-alerts.md).
 *
 * @param courseSlug - The public slug of the Course being played.
 * @param send - The tracker to report through, defaults to `track`.
 * @returns A reporter with `started` and `failed` methods that send at most once per key.
 */
export const createPlaybackReporter = (
  courseSlug: string,
  send: typeof track = track
) => {
  const started = new Set<string>();
  const failed = new Set<string>();

  return {
    started(itemId: string): void {
      if (started.has(itemId)) {
        return;
      }

      started.add(itemId);
      send("academy_playback_started", { courseSlug });
    },
    failed(itemId: string, reason: PlaybackErrorReason): void {
      const key = `${itemId}:${reason}`;

      if (failed.has(key)) {
        return;
      }

      failed.add(key);
      send("academy_playback_error", { courseSlug, reason });
    },
  };
};
